import { getTranslation } from "../config/translations";
import { EngineFactory } from "./EngineFactory";
import { FlowProcessor } from "./FlowProcessor";

export default class CommandFlowEngine {
  constructor(commands = {}, translations = {}, store = null) {
    this.commands = commands;
    this.translations = translations;

    // Stack of parent command names across redirects. Used by the "back"
    // handler to return to the previous command when the user is at the first
    // prompted step of a redirected command. Cleared on user-initiated
    // (non-redirect) startCommand calls.
    this.redirectParentChain = [];

    // Initialize all components using factory
    const components = EngineFactory.createComponents(
      commands,
      translations,
      store
    );
    Object.assign(this, components);

    // Create flow processor
    this.flowProcessor = new FlowProcessor(
      this.flowOrchestrator,
      this.sessionCoordinator,
      this.stepFlowManager
    );

    // Set engine reference
    this.sessionCoordinator.setEngineReference(this);
  }

  // === PUBLIC API ===
  isInSession() {
    return this.sessionCoordinator.getSessionInfo().isInSession;
  }
  getCurrentStepIndex() {
    return this.sessionCoordinator.getSessionInfo().currentStepIndex;
  }
  getTotalSteps() {
    return this.sessionCoordinator.getSessionInfo().totalSteps;
  }
  get session() {
    return this.sessionManager.session;
  }
  get currentCommandName() {
    return this.sessionManager.currentCommandName;
  }
  t(key, replacements = {}) {
    return getTranslation(key, this.translations, replacements);
  }
  endSession() {
    this.sessionCoordinator.endSession();
  }

  // === MAIN FLOW METHODS ===
  async startCommand(commandName, initialData = {}, options = {}) {
    const command = this.commands[commandName];
    if (!command) return { error: this.t("unknownCommand", { commandName }) };

    // User-initiated command starts reset the redirect parent chain so that
    // "back" from a fresh command does not pop into a stale previous chain.
    // Redirect-initiated starts pass { isRedirect: true } to preserve it.
    if (!options.isRedirect) {
      this.redirectParentChain = [];
    }

    // Handle stepless commands
    if (this.sessionCoordinator.isSteplessCommand(command)) {
      // If there's an existing session from a step-based command that redirected
      // here (e.g. questionnaire_complete via redirect from qst_q2), end it.
      // Stepless commands don't create their own session, so the stale session
      // would make isInSession() return true and prevent proper cleanup.
      if (this.isInSession()) {
        this.sessionManager.endSession();
      }
      // Set currentCommandName and sessionData for stepless commands
      this.sessionManager.currentCommandName = commandName;
      // Store initialData so it's available for onCommandComplete callback
      this.sessionManager.steplessSessionData = initialData || {};
      console.log(
        "🔍 Set currentCommandName for stepless command:",
        commandName
      );

      const callbacks = this.sessionCoordinator.createCallbackTriggers(
        commandName,
        initialData
      );
      const result = await this.commandProcessor.processSteplessCommand(
        command,
        commandName,
        initialData,
        callbacks
      );

      console.log("🔍 Stepless command result:", result);
      console.log(
        "🔍 currentCommandName before returning:",
        this.currentCommandName
      );

      // Don't clear currentCommandName here - it will be cleared when the next command starts
      // or by the completion handler after onCommandComplete callback is invoked

      return result;
    }

    // Initialize session and process first step
    const { sessionData, callbacks } =
      this.sessionCoordinator.initializeSession(
        commandName,
        command,
        initialData
      );

    const introMessage = await this.promptResolver.buildIntroMessage(
      command,
      sessionData,
      callbacks
    );
    const result = await this.stepFlowManager.processCurrentStep(introMessage);

    // If the result has output but no prompt, check if command completed
    if (result && result.output && !result.prompt && !result.nextCommand) {
      const sessionInfo = this.sessionCoordinator.getSessionInfo();
      console.log(
        "🔍 CommandFlowEngine.startCommand - checking if session should end:",
        {
          hasOutput: !!result.output,
          hasPrompt: !!result.prompt,
          hasNextCommand: !!result.nextCommand,
          hasMoreSteps: sessionInfo.hasMoreSteps,
          isInSession: this.isInSession(),
        }
      );

      // If no more steps and no redirect, this command completed
      if (!sessionInfo.hasMoreSteps) {
        console.log(
          "🏁 CommandFlowEngine.startCommand - Ending session after command completion"
        );
        this.endSession();
      }
    }

    return result;
  }

  async handleInput(input) {
    if (!this.isInSession()) return { error: "No active command session." };

    const sessionInfo = this.sessionCoordinator.getSessionInfo();

    // If no current step (command completed), end session and return
    if (!sessionInfo.currentStep) {
      this.endSession();
      return { error: "Command session has already completed." };
    }

    const callbacks = this.sessionCoordinator.createCallbackTriggers(
      this.currentCommandName,
      sessionInfo.sessionData
    );

    const inputResult = await this.flowOrchestrator.processUserInput(
      input,
      sessionInfo.currentStep,
      sessionInfo.sessionData,
      callbacks,
      this.promptResolver
    );

    if (inputResult.type === "validationError") return inputResult.result;

    // Record the current prompted step for back navigation, then advance
    this.sessionManager.recordPromptedStep(sessionInfo.currentStepIndex);
    this.sessionCoordinator.moveToNextStep();
    return await this.flowProcessor.processPostInputFlow(
      sessionInfo.currentStep,
      sessionInfo.sessionData,
      input,
      inputResult.stepOutput,
      this,
      callbacks
    );
  }
}
